import MessageAttachments from "./MessageAttachments";
import { parseMentions } from "@/lib/mentions";

/**
 * Single message bubble. Sender messages are right-aligned with a honey tint;
 * others are left-aligned neutral. Minimal, calm — not "consumer chat" loud.
 * Attachments render below the body, and we suppress the "📎" placeholder
 * body that Thread.jsx substitutes when the user only sent files.
 *
 * `viewerProfileId` — used to highlight `@you` mentions distinctly from
 * other mentions, so the viewer's eye lands on their own name without
 * reading the whole thread.
 */
export default function MessageBubble({ message, isMine, authorName, viewerProfileId }) {
  const time = new Date(message.created_at).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const attachments = message.message_attachments || [];
  // Hide the paperclip placeholder — it exists only to satisfy the
  // messages.body length check when a message is attachment-only.
  const displayBody =
    message.body === "📎" && attachments.length > 0 ? "" : message.body;

  const segments = displayBody ? parseMentions(displayBody) : [];
  const mentionsMe = segments.some(
    (s) => s.type === "mention" && s.profileId === viewerProfileId,
  );

  return (
    <div
      data-testid={`message-bubble-${message.id}`}
      data-mine={isMine ? "1" : "0"}
      data-mentions-me={mentionsMe ? "1" : "0"}
      className={`flex ${isMine ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed sm:max-w-[70%] ${
          isMine
            ? "rounded-br-md bg-primary/15 text-neutral-900"
            : mentionsMe
              ? "rounded-bl-md border border-honey-bright/50 bg-cream text-neutral-900"
              : "rounded-bl-md border border-neutral-200 bg-white text-neutral-900"
        }`}
      >
        {!isMine && authorName && (
          <p className="mb-0.5 text-[11px] font-semibold text-primary-hover">
            {authorName}
          </p>
        )}
        {displayBody && (
          <p className="whitespace-pre-wrap break-words">
            {segments.map((seg, i) => {
              if (seg.type === "text") {
                return <span key={i}>{seg.text}</span>;
              }
              const isYou = seg.profileId === viewerProfileId;
              return (
                <span
                  key={i}
                  data-testid="message-mention-pill"
                  data-mention-you={isYou ? "1" : "0"}
                  data-mention-profile-id={seg.profileId}
                  className={`mx-0.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[12px] font-semibold ${
                    isYou
                      ? "bg-honey-bright text-acorn"
                      : "bg-primary/15 text-primary-hover"
                  }`}
                >
                  @{seg.name}
                </span>
              );
            })}
          </p>
        )}
        {attachments.length > 0 && (
          <MessageAttachments attachments={attachments} isMine={isMine} />
        )}
        <p className="mt-1 select-none text-right text-[10px] leading-none text-neutral-400">{time}</p>
      </div>
    </div>
  );
}
